<template>
  <div
    role="main"
    aria-label="Tableau de bord des localités logistiques"
    tabindex="-1"
  >
    <p class="sr-only">
      Ce tableau de bord permet de gérer les localités logistiques, d’appliquer des filtres avancés, et d’effectuer des actions en masse. Utilisez les filtres pour affiner la recherche et les boutons d’action pour gérer les localités.
    </p>
    <!-- Dashboard metrics - Using standard B-Card components instead -->
    <b-row
      class="match-height mb-2"
      aria-label="Statistiques logistiques des localités"
    >
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card
          class="text-center bg-light-primary"
          aria-label="Nombre total de localités"
        >
          <div class="mb-1">
            <feather-icon
              icon="MapPinIcon"
              size="21"
              class="text-primary"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalRecords }}
          </h2>
          <p class="card-text">
            Total des localités
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card
          class="text-center bg-light-success"
          aria-label="Nombre de localités actives"
        >
          <div class="mb-1">
            <feather-icon
              icon="CheckCircleIcon"
              size="21"
              class="text-success"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ activeCount }}
          </h2>
          <p class="card-text">
            Localités actives
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card
          class="text-center bg-light-warning"
          aria-label="Nombre total de commandes logistiques"
        >
          <div class="mb-1">
            <feather-icon
              icon="PackageIcon"
              size="21"
              class="text-warning"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalOrders }}
          </h2>
          <p class="card-text">
            Total commandes
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card
          class="text-center bg-light-info"
          aria-label="Moyenne de commandes par localité"
        >
          <div class="mb-1">
            <feather-icon
              icon="TruckIcon"
              size="21"
              class="text-info"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ averageOrdersPerLocality }}
          </h2>
          <p class="card-text">
            Moyenne commandes/localité
          </p>
        </b-card>
      </b-col>
    </b-row>
    <!-- Simplified Filters UI to match the image -->
    <b-card
      class="mb-2"
      aria-label="Filtres avancés pour la gestion des localités"
    >
      <b-card-title>Filtres avancés <span class="sr-only">pour la gestion logistique des localités</span></b-card-title>
      <b-row>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group
            label="Délégation"
            aria-label="Filtrer par délégation logistique"
          >
            <v-select
              v-model="filters.delegation"
              :options="delegationOptions"
              placeholder="Sélectionner une délégation"
              :clearable="true"
              :searchable="true"
              label="name"
              class="select-search-option"
              :reduce="option => option"
              @input="applyFilters"
              @search:blur="() => {}"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group
            label="État"
            aria-label="Filtrer par état de la localité"
          >
            <v-select
              v-model="filters.active"
              :options="statusOptions"
              placeholder="Tous"
              :clearable="true"
              label="label"
              class="select-search-option"
              :reduce="option => option.value"
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Additional filters (toggleable) -->
      <div v-if="showMoreFilters">
        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Gouvernorat">
              <v-select
                v-model="filters.governorate"
                :options="governorateOptions"
                placeholder="Sélectionner un gouvernorat"
                :clearable="true"
                :searchable="true"
                label="name"
                class="select-search-option"
                :reduce="option => option"
                @input="onGovernorateChange"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Nombre de commandes">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.minOrders"
                  placeholder="Min"
                  type="number"
                  min="0"
                  class="mr-2"
                  @change="applyFilters"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.maxOrders"
                  placeholder="Max"
                  type="number"
                  min="0"
                  class="ml-2"
                  @change="applyFilters"
                />
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Date de création">
              <b-row>
                <b-col>
                  <b-form-datepicker
                    v-model="filters.createdAfter"
                    placeholder="À partir de"
                    locale="fr"
                    class="mb-2"
                    @input="applyFilters"
                  />
                </b-col>
                <b-col>
                  <b-form-datepicker
                    v-model="filters.createdBefore"
                    placeholder="Jusqu'à"
                    locale="fr"
                    @input="applyFilters"
                  />
                </b-col>
              </b-row>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div class="d-flex justify-content-between mt-1">
        <div>
          <b-button
            variant="outline-secondary"
            size="sm"
            :disabled="!hasActiveFilters"
            @click="resetFiltersAndRefresh"
          >
            <span>Réinitialiser</span>
          </b-button>
        </div>

        <div>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="toggleMoreFilters"
          >
            <span>{{ showMoreFilters ? 'Moins de filtres' : 'Plus de filtres' }}</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Header section with title and badge matching the image -->
    <div class="custom-search d-flex justify-content-between mb-2">
      <div class="d-flex align-items-center">
        <h3 class="mb-0 mr-2">
          Liste des Localités
        </h3>
        <b-badge
          variant="primary"
          pill
          class="badge-round"
        >
          {{ totalRecords }}
        </b-badge>
      </div>

      <div class="d-flex">
        <!-- Action buttons -->
        <keyboard-shortcut
          class="ml-1"
          shortcut="A"
          @shortcut="navigateToAdd"
        />
        <keyboard-shortcut
          class="ml-1"
          shortcut="F"
          @shortcut="focusSearch"
        />

        <!-- Admin only button -->
        <b-form-group
          v-if="user.role==='admin'"
          class="mb-0"
        >
          <b-button
            :to="{ name: 'localities-add' }"
            variant="primary"
            class="ml-1 d-inline-block"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter localité</span>
          </b-button>
        </b-form-group>

        <!-- Export button -->
        <b-dropdown
          variant="outline-secondary"
          right
          toggle-class="ml-2"
          text="Exporter"
        >
          <b-dropdown-item @click="exportTable('csv')">
            <feather-icon
              icon="FileTextIcon"
              size="16"
              class="mr-1"
            />
            <span>CSV</span>
          </b-dropdown-item>
          <b-dropdown-item @click="exportTable('xlsx')">
            <feather-icon
              icon="FileIcon"
              size="16"
              class="mr-1"
            />
            <span>Excel</span>
          </b-dropdown-item>
          <b-dropdown-item @click="exportTable('pdf')">
            <feather-icon
              icon="FileIcon"
              size="16"
              class="mr-1"
            />
            <span>PDF</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Loading states with skeletons -->
    <div
      v-if="isLoading"
      class="mb-2"
    >
      <b-card>
        <b-skeleton-table
          :rows="5"
          :columns="6"
          :table-props="{ bordered: true, striped: true }"
        />
      </b-card>
    </div>

    <!-- Enhanced table -->
    <vue-good-table
      v-else
      ref="localitiesTable"
      :columns="columns"
      :rows="localities"
      :search-options="{ enabled: true, externalQuery: searchTerm }"
      :pagination-options="{ enabled: true, perPage: pageLength, mode: 'records' }"
      :total-rows="totalRecords"
      :is-loading="isLoading"
      :sort-options="{ enabled: true, initialSortBy: {field: 'name', type: 'asc'} }"
      style-class="vgt-table bordered striped hover responsive"
      table-class="localities-table"
      :select-options="{ enabled: isAdmin, selectOnCheckboxOnly: true }"
      aria-label="Tableau des localités logistiques"
      @on-page-change="onPageChangeWithoutReload"
      @on-per-page-change="onPerPageChangeWithoutReload"
      @on-sort-change="onSortChangeWithoutReload"
      @on-selected-rows-change="onSelectedRowsChange"
    >
      <!-- Add table header slot for custom header styling -->
      <template
        slot="table-header"
        slot-scope="props"
      >
        <tr>
          <th
            v-for="column in props.columns"
            :key="column.label"
            :class="[
              column.sortable ? 'sorting-header' : '',
              column.thClass || '',
              column.field === props.sortingKey ? 'active-sorting' : ''
            ]"
            @click="props.sortingOptions.enabled && column.sortable ? props.sort(column.field) : null"
          >
            <div class="d-flex align-items-center">
              <span>{{ column.label }}</span>
              <span
                v-if="props.sortingOptions.enabled && column.sortable"
                class="sort-icon ml-1"
              >
                <feather-icon
                  v-if="column.field === props.sortingKey"
                  :icon="props.sortingDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                  size="14"
                />
                <feather-icon
                  v-else
                  icon="ArrowUpDownIcon"
                  size="14"
                  class="text-muted"
                />
              </span>
            </div>
          </th>
        </tr>
      </template>

      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Enhanced locality name column with link -->
        <span v-if="props.column.field === 'name'">
          <div class="d-flex align-items-center">
            <div
              v-b-tooltip.hover
              :title="`Code postal: ${props.row.zip_code || 'Non défini'}`"
              class="locality-name"
            >
              <router-link
                :to="{ name: 'localities-detail', params: { id: props.row.id }}"
                class="locality-link"
              >
                {{ props.formattedRow[props.column.field] }}
              </router-link>
              <b-badge
                v-if="props.row.zip_code"
                variant="light-primary"
                pill
                class="ml-1 locality-zip-badge"
              >
                {{ props.row.zip_code }}
              </b-badge>
            </div>
          </div>
        </span>

        <!-- Enhanced governorate & delegation columns with links -->
        <span v-else-if="props.column.field === 'governorate'">
          <router-link
            v-if="props.row.governorate_id"
            v-b-tooltip.hover
            :to="{ name: 'governorate-detail', params: { id: props.row.governorate_id }}"
            title="Voir détails du gouvernorat"
          >
            {{ props.formattedRow[props.column.field] }}
          </router-link>
          <span v-else>{{ props.formattedRow[props.column.field] }}</span>
        </span>

        <span v-else-if="props.column.field === 'delegation'">
          <router-link
            v-if="props.row.delegation_id"
            v-b-tooltip.hover
            :to="{ name: 'delegation-detail', params: { id: props.row.delegation_id }}"
            title="Voir détails de la délégation"
          >
            {{ props.formattedRow[props.column.field] }}
          </router-link>
          <span v-else>{{ props.formattedRow[props.column.field] }}</span>
        </span>

        <!-- Enhanced orders count column with mini chart -->
        <span v-else-if="props.column.field === 'num_orders'">
          <div class="d-flex align-items-center justify-content-center">
            <b-badge
              :variant="getOrdersCountVariant(props.row.num_orders)"
              pill
              class="orders-badge"
            >
              {{ props.row.num_orders }}
            </b-badge>
            <mini-line-chart
              v-if="props.row.order_trend && props.row.num_orders > 0"
              :data="props.row.order_trend || [0,0,0,0,0]"
              :color="getOrdersCountColor(props.row.num_orders)"
              class="ml-2"
              width="50"
              height="20"
            />
          </div>
        </span>

        <!-- Enhanced status column with animation -->
        <span v-else-if="props.column.field === 'is_active'">
          <div class="status-cell">
            <transition
              name="fade"
              mode="out-in"
            >
              <div
                v-if="props.row.is_active === true"
                key="active"
                class="d-flex flex-column align-items-center"
              >
                <b-badge
                  class="badge-glow bg-success mb-1 status-badge"
                  pill
                >
                  <feather-icon
                    icon="CheckIcon"
                    class="mr-25"
                    size="12"
                  />
                  <span>Activé</span>
                </b-badge>
              </div>
              <div
                v-else
                key="inactive"
                class="d-flex flex-column align-items-center"
              >
                <b-badge
                  class="badge-glow bg-danger mb-1 status-badge"
                  pill
                >
                  <feather-icon
                    icon="XIcon"
                    class="mr-25"
                    size="12"
                  />
                  <span>Désactivé</span>
                </b-badge>
              </div>
            </transition>
          </div>
        </span>

        <!-- Enhanced action column -->
        <span v-else-if="props.column.field === 'action'">
          <span>
            <router-link
              v-b-tooltip.hover
              class="text-info mr-1"
              :to="{ name: 'localities-detail', params: { id: props.row.id }}"
              title="Voir détails"
            >
              <feather-icon
                icon="EyeIcon"
                class="cursor-pointer"
                size="16"
              />
            </router-link>

            <router-link
              v-if="user.role === 'admin'"
              v-b-tooltip.hover
              class="text-warning mr-1"
              :to="{ name: 'localities-edit', params: { id: props.row.id }}"
              title="Modifier"
            >
              <feather-icon
                icon="EditIcon"
                class="cursor-pointer"
                size="16"
              />
            </router-link>

            <b-button
              v-if="user.role === 'admin'"
              v-b-tooltip.hover
              variant="link"
              class="btn-icon p-0"
              :disabled="toggleStatusLoading === props.row.id"
              :title="props.row.is_active ? 'Désactiver cette localité' : 'Activer cette localité'"
              @click="confirmToggleStatus(props.row.id, !props.row.is_active)"
            >
              <b-spinner
                v-if="toggleStatusLoading === props.row.id"
                small
                type="grow"
              />
              <feather-icon
                v-else
                :icon="props.row.is_active ? 'XIcon' : 'CheckIcon'"
                :class="props.row.is_active ? 'text-danger' : 'text-success'"
                size="18"
              />
            </b-button>
          </span>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- Empty state template - New addition -->
      <template slot="emptystate">
        <div class="empty-state text-center py-5">
          <feather-icon
            icon="MapPinIcon"
            size="48"
            class="text-muted mb-2"
          />
          <h4>Aucune localité trouvée</h4>
          <p class="text-muted">
            {{ searchTerm ? 'Aucune localité correspondant à votre recherche.' : 'Aucune localité disponible.' }}
          </p>
          <div v-if="hasActiveFilters">
            <p>Essayez de modifier vos filtres</p>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="resetFilters"
            >
              Réinitialiser les filtres
            </b-button>
          </div>
        </div>
      </template>

      <!-- Add bulk actions for selected rows -->
      <template slot="selected-row-actions">
        <div class="selected-actions p-2 bg-light-primary rounded d-flex align-items-center">
          <span class="mr-2">{{ selectedRows.length }} sélectionné(s)</span>
          <b-button
            v-if="isAdmin && selectedRows.length > 0"
            variant="outline-success"
            size="sm"
            class="mr-1"
            @click="bulkActivate"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="14"
              class="mr-1"
            />
            Activer
          </b-button>
          <b-button
            v-if="isAdmin && selectedRows.length > 0"
            variant="outline-danger"
            size="sm"
            @click="bulkDeactivate"
          >
            <feather-icon
              icon="XCircleIcon"
              size="14"
              class="mr-1"
            />
            Désactiver
          </b-button>
          <b-button
            variant="link"
            size="sm"
            class="ml-2"
            @click="clearSelection"
          >
            Annuler
          </b-button>
        </div>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap d-none d-sm-inline">
              Affichage
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> de {{ props.total }} résultat{{ props.total > 1 ? 's' : '' }} trouvé{{ props.total > 1 ? 's' : '' }} </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Status confirmation modal -->
    <b-modal
      id="confirm-status-modal"
      ref="confirmStatusModal"
      :title="statusModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      aria-label="Confirmation de changement de statut de la localité"
      @ok="handleStatusConfirmed"
      @cancel="cancelStatusChange"
    >
      <p>{{ statusModalMessage }}</p>
    </b-modal>

    <!-- New bulk action confirmation modal -->
    <b-modal
      id="confirm-bulk-modal"
      ref="confirmBulkModal"
      :title="bulkModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      aria-label="Confirmation d’action en masse sur les localités"
      @ok="handleBulkConfirmed"
      @cancel="cancelBulkChange"
    >
      <p>{{ bulkModalMessage }}</p>
      <ul v-if="selectedRows.length <= 5">
        <li
          v-for="row in selectedRows"
          :key="row.id"
        >
          {{ row.name }}
        </li>
      </ul>
      <p v-else>
        {{ selectedRows.length }} localités seront affectées.
      </p>
    </b-modal>

    <!-- Keyboard shortcut modal -->
    <b-modal
      id="keyboard-shortcuts-modal"
      title="Raccourcis clavier"
      hide-footer
      aria-label="Raccourcis clavier pour la gestion des localités"
    >
      <b-list-group flush>
        <b-list-group-item>
          <strong>Ctrl + F</strong>: Focus sur la recherche
        </b-list-group-item>
        <b-list-group-item>
          <strong>Ctrl + A</strong>: Ajouter une nouvelle localité
        </b-list-group-item>
        <b-list-group-item>
          <strong>Echap</strong>: Effacer la recherche
        </b-list-group-item>
        <b-list-group-item>
          <strong>Ctrl + /</strong>: Afficher les raccourcis clavier
        </b-list-group-item>
      </b-list-group>
      <div class="mt-3 text-center">
        <b-button
          variant="primary"
          @click="$bvModal.hide('keyboard-shortcuts-modal')"
        >
          Fermer
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BSpinner,
  BCard,
  BCardTitle,
  BRow,
  BCol,
  BBadge,
  BFormDatepicker,
  BSkeletonTable,
  BDropdown,
  BDropdownItem,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'
import MiniLineChart from '@/components/charts/MiniLineChart.vue'
import KeyboardShortcut from '@/components/KeyboardShortcut.vue'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BBadge,
    BFormDatepicker,
    vSelect,
    MiniLineChart,
    KeyboardShortcut,
    BSkeletonTable,
    BDropdown,
    BDropdownItem,
    BListGroup,
    BListGroupItem,
  },
  data() {
    return {
      isLoading: false,
      pageLength: 100,
      user: {},
      columns: [
        {
          label: 'Localité',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'Localité',
          },
          thClass: 'min-width-200',
          tdClass: 'min-width-200',
          sortable: true,
        },
        {
          label: 'Code Postal',
          field: 'zip_code',
          filterOptions: {
            enabled: true,
            placeholder: 'Code Postal',
          },
          thClass: 'min-width-150',
          tdClass: 'min-width-150',
          sortable: true,
        },
        {
          label: 'Délégation',
          field: 'delegation',
          filterOptions: {
            enabled: true,
            placeholder: 'Délégation',
          },
          thClass: 'min-width-200',
          tdClass: 'min-width-200',
          sortable: true,
          responsive: true,
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Gouvernorat',
          },
          thClass: 'min-width-200',
          tdClass: 'min-width-200',
          sortable: true,
          responsive: true,
        },
        {
          label: 'N° colis',
          field: 'num_orders',
          filterOptions: {
            enabled: true,
            placeholder: 'N° colis',
          },
          thClass: 'text-center min-width-100',
          tdClass: 'text-center min-width-100',
          sortable: true,
        },
        {
          label: 'État',
          field: 'is_active',
          filterOptions: {
            enabled: true,
          },
          thClass: 'text-center min-width-150',
          tdClass: 'text-center min-width-150',
          sortable: true,
        },
        {
          label: 'Action',
          field: 'action',
          thClass: 'text-center min-width-150',
          tdClass: 'text-center min-width-150',
          sortable: false,
        },
      ],
      localities: [],
      searchTerm: '',
      showMoreFilters: false,
      totalRecords: 0,
      currentPage: 1,
      toggleStatusLoading: null,
      localityToToggle: null,
      newStatus: null,
      statusModalTitle: '',
      statusModalMessage: '',

      // Advanced filters
      filters: {
        name: '',
        zip_code: '',
        delegation: null,
        governorate: null,
        active: null,
        minOrders: null,
        maxOrders: null,
        createdAfter: null,
        createdBefore: null,
      },

      // Filter options
      statusOptions: [
        { value: true, label: 'Actif' },
        { value: false, label: 'Inactif' },
      ],
      delegationOptions: [],
      allDelegations: [], // Store all delegations for filtering
      governorateOptions: [],
      sortBy: 'name',
      sortType: 'asc',
      // Additional statistics fields
      activeCount: 0,
      inactiveCount: 0,
      totalOrders: 0,

      // For export functionality
      exportFields: ['name', 'zip_code', 'delegation', 'governorate', 'num_orders', 'is_active'],
      exportLabels: {
        name: 'Localité',
        zip_code: 'Code Postal',
        delegation: 'Délégation',
        governorate: 'Gouvernorat',
        num_orders: 'N° colis',
        is_active: 'État',
      },

      // New data properties
      selectedRows: [],
      bulkModalTitle: '',
      bulkModalMessage: '',
      bulkAction: null,
      showKeyboardShortcutsModal: false,

      // Add a flag to track if a request is in progress
      isRequestPending: false,
      lastRequestTime: 0,

      // Add tracking variables for filter state
      initialFiltersSet: false,
      filterSnapshot: {},
      filtersChanged: false,
      initialLoad: true,
      dataInitialized: false,

      // Add tracking variables
      initialDataLoaded: false,
      fetchTimerId: null, // Changed from _fetchTimer to fetchTimerId
    }
  },
  computed: {
    averageOrdersPerLocality() {
      if (!this.localities || this.localities.length === 0) return '0'
      return (this.totalOrders / this.localities.length).toFixed(2)
    },

    // New computed properties
    hasActiveFilters() {
      return (
        this.filters.delegation !== null
        || this.filters.governorate !== null
        || this.filters.active !== null
        || this.filters.minOrders !== null
        || this.filters.maxOrders !== null
        || this.filters.createdAfter !== null
        || this.filters.createdBefore !== null
      )
    },

    activeFilterCount() {
      let count = 0
      if (this.filters.delegation) count += 1
      if (this.filters.governorate) count += 1
      if (this.filters.active !== null) count += 1
      if (this.filters.minOrders || this.filters.maxOrders) count += 1
      if (this.filters.createdAfter || this.filters.createdBefore) count += 1
      return count
    },

    isAdmin() {
      return this.user && this.user.role === 'admin'
    },
  },
  created() {
    this.user = storeAuth.state.user

    // Simplify initialization to just one call
    this.initData()
  },
  methods: {
    // New initialization method that safely loads data in sequence
    async initData() {
      try {
        // Only fetch dropdown data first
        await Promise.all([
          this.loadGovernorateOptions(),
          this.loadDelegationOptions(),
        ])

        // Take snapshot of initial filter state
        this.takeFilterSnapshot()

        // Only load data once on initialization to prevent loops
        if (!this.initialDataLoaded) {
          await this.getLocalitiesWithDebounce()
          this.initialDataLoaded = true
        }
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors de l\'initialisation')
      }
    },

    // Special version with debounce for initialization
    async getLocalitiesWithDebounce() {
      if (this.fetchTimerId) { // Changed from this._fetchTimer
        clearTimeout(this.fetchTimerId) // Changed from this._fetchTimer
      }

      return new Promise(resolve => {
        this.fetchTimerId = setTimeout(async () => { // Changed from this._fetchTimer
          await this.getLocalities()
          resolve()
        }, 300)
      })
    },

    // Replace existing pagination handlers with these versions that don't trigger API calls
    onPageChangeWithoutReload(params) {
      this.currentPage = params.currentPage
      // Don't call getLocalities() here - prevents the loop
    },

    onPerPageChangeWithoutReload(params) {
      this.pageLength = params.currentPerPage
      // Don't call getLocalities() here - prevents the loop
    },

    onSortChangeWithoutReload(params) {
      this.sortBy = params.sortBy
      this.sortType = params.sortType
      // Don't call getLocalities() here - prevents the loop
    },

    // Add a manual refresh button handler if needed
    refreshData() {
      this.getLocalities()
    },

    // Store current filters state for comparison
    takeFilterSnapshot() {
      this.filterSnapshot = JSON.parse(JSON.stringify(this.filters))
    },

    // Check if filters have changed from last snapshot
    checkIfFiltersChanged() {
      const currentFilters = JSON.stringify(this.filters)
      const lastFilters = JSON.stringify(this.filterSnapshot)
      this.filtersChanged = currentFilters !== lastFilters
    },

    // Mark filters as changed without refreshing
    prepareRefresh() {
      this.checkIfFiltersChanged()
    },

    // Explicitly apply filters
    applyFilters() {
      this.getLocalities()
      this.filtersChanged = false
      this.takeFilterSnapshot()
    },

    // Toggle more filters
    toggleMoreFilters() {
      this.showMoreFilters = !this.showMoreFilters
    },

    async loadGovernorateOptions() {
      try {
        const { data } = await axios.get('/api/governorates/')
        this.governorateOptions = data || []
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des gouvernorats')
        this.governorateOptions = []
      }
    },

    async loadDelegationOptions() {
      try {
        const { data } = await axios.get('/api/delegations/')
        this.delegationOptions = data || []
        this.allDelegations = data || [] // Store all delegations
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des délégations')
        this.delegationOptions = []
        this.allDelegations = []
      }
    },

    // Modified method that updates UI but doesn't trigger API call
    onGovernorateChangeNoRefresh(governorate) {
      if (this.filters.delegation) {
        this.filters.delegation = null
      }

      if (governorate) {
        this.delegationOptions = this.delegationOptions.filter(
          delegation => delegation.governorate && delegation.governorate.id === governorate.id,
        )
      } else {
        // Load all delegations without triggering API
        this.loadDelegationOptions()
      }

      this.checkIfFiltersChanged()
    },

    // New method that filters delegations and applies filters
    async onGovernorateChange(governorate) {
      // Reset delegation filter when governorate changes
      if (this.filters.delegation) {
        this.filters.delegation = null
      }

      // Store all delegations before filtering
      if (!this.allDelegations || this.allDelegations.length === 0) {
        const { data } = await axios.get('/api/delegations/')
        this.allDelegations = data || []
      }

      if (governorate) {
        // Filter delegations by selected governorate
        this.delegationOptions = this.allDelegations.filter(
          delegation => delegation.governorate && delegation.governorate.id === governorate.id,
        )
      } else {
        // Restore all delegations when governorate is cleared
        this.delegationOptions = [...this.allDelegations]
      }

      // Apply filters to fetch data
      this.applyFilters()
    },

    // Resets filters and forces refresh
    resetFiltersAndRefresh() {
      this.filters = {
        name: '',
        zip_code: '',
        delegation: null,
        governorate: null,
        active: null,
        minOrders: null,
        maxOrders: null,
        createdAfter: null,
        createdBefore: null,
      }
      this.loadDelegationOptions()
      this.getLocalities()
      this.filtersChanged = false
      this.takeFilterSnapshot()
    },

    // Get localities with applied filters - add logging
    async getLocalities() {
      // Prevent concurrent requests
      if (this.isRequestPending) {
        return
      }

      this.isRequestPending = true
      this.isLoading = true

      try {
        let url = '/api/localities/detailed/'

        // Add query params for filters
        const params = new URLSearchParams()
        if (this.filters.name) params.append('name__icontains', this.filters.name)
        if (this.filters.zip_code) params.append('zip_code__icontains', this.filters.zip_code)
        if (this.filters.delegation) params.append('delegation', this.filters.delegation.id)
        if (this.filters.governorate) params.append('governorate', this.filters.governorate.id)
        if (this.filters.active !== null) params.append('is_active', this.filters.active)

        // Add new filter params
        if (this.filters.minOrders !== null && this.filters.minOrders !== '') {
          params.append('min_orders', parseInt(this.filters.minOrders, 10))
        }
        if (this.filters.maxOrders !== null && this.filters.maxOrders !== '') {
          params.append('max_orders', parseInt(this.filters.maxOrders, 10))
        }

        // Add date filters with proper formatting
        if (this.filters.createdAfter) {
          params.append('created_after', this.formatDate(this.filters.createdAfter))
        }
        if (this.filters.createdBefore) {
          params.append('created_before', this.formatDate(this.filters.createdBefore))
        }

        // Add sorting params
        if (this.sortBy) {
          const sortField = this.sortBy === 'num_orders' ? 'num_orders' : this.sortBy
          const sortDirection = this.sortType === 'desc' ? '-' : ''
          params.append('ordering', `${sortDirection}${sortField}`)
        }

        // Add pagination params
        params.append('limit', this.pageLength)
        params.append('offset', (this.currentPage - 1) * this.pageLength)

        // Append params to URL if any exist
        if (params.toString()) url = `${url}?${params.toString()}`

        const { data } = await axios.get(url)

        // Handle paginated response from backend
        if (data.results) {
          this.localities = data.results
          this.totalRecords = data.count
        } else {
          this.localities = data
          this.totalRecords = data.length
        }

        // Calculate statistics after data load
        this.$nextTick(() => {
          this.calculateStatistics()
        })

        // Update filter snapshot after successful fetch
        this.takeFilterSnapshot()
        this.filtersChanged = false
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des localités')
      } finally {
        this.isLoading = false
        this.isRequestPending = false
        this.initialLoad = false
      }
    },

    // Format date for API requests
    formatDate(date) {
      if (!date) return null

      // Convert to YYYY-MM-DD format
      if (typeof date === 'string' && date.includes('-')) {
        return date // Already in correct format
      }

      const d = new Date(date)
      let month = `${d.getMonth() + 1}`
      let day = `${d.getDate()}`
      const year = d.getFullYear()

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`

      return [year, month, day].join('-')
    },

    // Reset filters
    resetFilters() {
      this.filters = {
        name: '',
        zip_code: '',
        delegation: null,
        governorate: null,
        active: null,
        minOrders: null,
        maxOrders: null,
        createdAfter: null,
        createdBefore: null,
      }
      this.loadDelegationOptions()
      this.getLocalities()
    },

    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },

    onPageChange(params) {
      this.currentPage = params.currentPage
      this.getLocalities()
    },

    onPerPageChange(params) {
      this.pageLength = params.currentPerPage
      this.getLocalities()
    },

    confirmToggleStatus(id, makeActive) {
      this.localityToToggle = id
      this.newStatus = makeActive

      this.statusModalTitle = makeActive ? 'Activer la localité' : 'Désactiver la localité'
      this.statusModalMessage = makeActive
        ? 'Êtes-vous sûr de vouloir activer cette localité? Cela permettra de l\'utiliser pour les livraisons.'
        : 'Êtes-vous sûr de vouloir désactiver cette localité? Cela empêchera de l\'utiliser pour les livraisons.'

      this.$refs.confirmStatusModal.show()
    },

    cancelStatusChange() {
      this.localityToToggle = null
      this.newStatus = null
    },

    handleStatusConfirmed() {
      if (this.localityToToggle !== null) {
        this.toggleStatus(this.localityToToggle, this.newStatus)
      }
    },

    async toggleStatus(id, makeActive) {
      try {
        this.toggleStatusLoading = id
        await axios.post(`/api/localities/toggle-status/${id}/`, { is_active: makeActive })

        // Update the item in the localities array to avoid a full reload
        const index = this.localities.findIndex(locality => locality.id === id)
        if (index !== -1) {
          this.localities[index].is_active = makeActive
        }

        this.showToast(
          'success',
          'top-center',
          `La localité a été ${makeActive ? 'activée' : 'désactivée'} avec succès`,
        )
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      } finally {
        this.toggleStatusLoading = null
        this.localityToToggle = null
        this.newStatus = null
      }
    },

    // New methods
    getOrdersCountVariant(count) {
      if (count === 0) return 'light-secondary'
      if (count < 10) return 'light-info'
      if (count < 50) return 'light-primary'
      if (count < 100) return 'light-warning'
      return 'light-success'
    },

    getOrdersCountColor(count) {
      if (count === 0) return '#b8c2cc'
      if (count < 10) return '#00cfe8'
      if (count < 50) return '#7367f0'
      if (count < 100) return '#ff9f43'
      return '#28c76f'
    },

    // Handle table sorting
    onSortChange(params) {
      this.sortBy = params.sortBy
      this.sortType = params.sortType
      this.getLocalities()
    },

    // Calculate statistics
    calculateStatistics() {
      this.activeCount = this.localities.filter(l => l.is_active).length
      this.inactiveCount = this.localities.filter(l => !l.is_active).length
      this.totalOrders = this.localities.reduce((sum, l) => sum + (l.num_orders || 0), 0)
    },

    // Export table data
    async exportTable(format) {
      // Prepare data for export
      const exportData = this.localities.map(item => {
        const row = {}
        this.exportFields.forEach(field => {
          if (field === 'is_active') {
            row[this.exportLabels[field]] = item[field] ? 'Activé' : 'Désactivé'
          } else {
            row[this.exportLabels[field]] = item[field]
          }
        })
        return row
      })

      try {
        if (format === 'csv') {
          // CSV export logic
          let csvContent = ''

          // Header row
          const headers = this.exportFields.map(field => this.exportLabels[field])
          csvContent += `${headers.join(',')}\n`

          // Data rows
          exportData.forEach(item => {
            const row = this.exportFields.map(field => {
              // Handle fields that might contain commas
              const value = item[this.exportLabels[field]]
              if (typeof value === 'string' && value.includes(',')) {
                return `"${value}"`
              }
              return value
            })
            csvContent += `${row.join(',')}\n`
          })

          // Create blob and download
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement('a')
          const url = URL.createObjectURL(blob)
          link.setAttribute('href', url)
          link.setAttribute('download', `localities_${new Date().toISOString().split('T')[0]}.csv`)
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          this.showToast('success', 'top-center', 'Export CSV réussi')
        } else if (format === 'xlsx') {
          // If using a library like SheetJS for Excel export
          // This is a placeholder - you would need to include the library
          this.showToast('info', 'top-center', 'Fonctionnalité Excel en cours de développement')
        } else if (format === 'pdf') {
          try {
            // Placeholder for PDF export functionality
            this.showToast('info', 'top-center', 'Export PDF en cours de développement')
          } catch (error) {
            this.showToast('danger', 'top-center', 'Erreur lors de l\'export en PDF')
          }
        }
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors de l\'export')
      }
    },

    handleKeyDown(e) {
      // Ctrl+F: Focus search
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault()
        this.focusSearch()
      }
      // Ctrl+A: Add new locality (admin only)
      if (e.ctrlKey && e.key === 'a' && this.isAdmin) {
        e.preventDefault()
        this.navigateToAdd()
      }
      // Ctrl+/: Show keyboard shortcuts
      if (e.ctrlKey && e.key === '/') {
        e.preventDefault()
        this.$bvModal.show('keyboard-shortcuts-modal')
      }
    },

    handleEscKey(e) {
      if (e.key === 'Escape' && document.activeElement !== this.$refs.searchInput) {
        this.searchTerm = ''
      }
    },

    focusSearch() {
      this.$nextTick(() => {
        this.$refs.searchInput.$el.focus()
      })
    },

    navigateToAdd() {
      this.$router.push({ name: 'localities-add' })
    },

    removeFilter(filterName) {
      this.filters[filterName] = null
      this.prepareRefresh() // Mark as changed but don't refresh yet
    },

    onSelectedRowsChange(params) {
      this.selectedRows = params.selectedRows
    },

    clearSelection() {
      this.$refs.localitiesTable.unselectAllRows()
      this.selectedRows = []
    },

    bulkActivate() {
      this.bulkModalTitle = 'Activer les localités sélectionnées'
      this.bulkModalMessage = 'Êtes-vous sûr de vouloir activer toutes les localités sélectionnées?'
      this.bulkAction = 'activate'
      this.$refs.confirmBulkModal.show()
    },

    bulkDeactivate() {
      this.bulkModalTitle = 'Désactiver les localités sélectionnées'
      this.bulkModalMessage = 'Êtes-vous sûr de vouloir désactiver toutes les localités sélectionnées?'
      this.bulkAction = 'deactivate'
      this.$refs.confirmBulkModal.show()
    },

    cancelBulkChange() {
      this.bulkAction = null
    },

    async handleBulkConfirmed() {
      if (!this.bulkAction) return

      const isActivate = this.bulkAction === 'activate'
      const ids = this.selectedRows.map(row => row.id)

      try {
        this.isLoading = true
        await axios.post('/api/localities/bulk-toggle-status/', {
          ids,
          is_active: isActivate,
        })

        // Update the status in the local data
        ids.forEach(id => {
          const index = this.localities.findIndex(locality => locality.id === id)
          if (index !== -1) {
            this.localities[index].is_active = isActivate
          }
        })

        this.clearSelection()
        this.showToast(
          'success',
          'top-center',
          `${ids.length} localités ont été ${isActivate ? 'activées' : 'désactivées'} avec succès`,
        )
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors de la mise à jour du statut')
      } finally {
        this.isLoading = false
        this.bulkAction = null
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import "@core/scss/vue/libs/vue-select.scss";

// Enhanced table styles
.localities-table {
  .vgt-table {
    border-collapse: separate;
    border-spacing: 0;

    th {
      position: relative;
      font-weight: 600;

      &.active-sorting {
        background-color: rgba($color: #7367f0, $alpha: 0.1);
        color: #7367f0;
      }

      &.sorting-header {
        cursor: pointer;

        &:hover {
          background-color: rgba($color: #7367f0, $alpha: 0.05);
        }
      }
    }

    td {
      vertical-align: middle;
    }
  }

  .locality-name {
    .locality-link {
      color: #ea5455 !important;
      font-weight: 800;
    }

    .locality-zip-badge {
      font-weight: 800;
    }
  }

  // Status badges
  .badge-glow {
    &.bg-success {
      box-shadow: 0 0 10px rgba(40, 199, 111, 0.6);
    }

    &.bg-danger {
      box-shadow: 0 0 10px rgba(234, 84, 85, 0.6);
    }
  }

  // Summary statistics panel
  .summary-statistics {
    background-color: rgba($color: #f8f8f8, $alpha: 0.5);
    border-top: 1px solid #ebe9f1;
    font-size: 0.95rem;
  }
}

// Responsive table styles
.vgt-table {
  min-width: 800px;

  @media (max-width: 992px) {
    .min-width-200 {
      min-width: 150px;
    }

    .min-width-150 {
      min-width: 120px;
    }

    .min-width-100 {
      min-width: 80px;
    }
  }

  @media (max-width: 768px) {
    .min-width-200 {
      min-width: 120px;
    }

    .min-width-150 {
      min-width: 100px;
    }

    .min-width-100 {
      min-width: 60px;
    }
  }
}

// Improve button styling in action column
.btn-icon {
  padding: 0.25rem 0.5rem;

  @media (max-width: 576px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.8rem;
  }
}

// Search box styling
.search-box {
  position: relative;

  .search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #b8c2cc;
    z-index: 2;
  }

  .clear-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #b8c2cc;
    z-index: 2;

    &:hover {
      color: #7367f0;
    }
  }

  input {
    padding-left: 35px;
    padding-right: 35px;
  }
}

// Active filters styling
.active-filters {
  .badge {
    display: inline-flex;
    align-items: center;
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;

    svg {
      margin-left: 0.5rem;
      cursor: pointer;
    }

    &:hover {
      background-color: rgba($color: #7367f0, $alpha: 0.8);
    }
  }
}

// Empty state styling
.empty-state {
  padding: 3rem 1rem;

  svg {
    opacity: 0.6;
  }
}

// Animations
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

// Add styles for statistic cards
.card {
  .text-center {
    min-height: 120px;
  }

  h2 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  &.bg-light-primary {
    background-color: rgba($color: #7367f0, $alpha: 0.12) !important;
  }

  &.bg-light-success {
    background-color: rgba($color: #28c76f, $alpha: 0.12) !important;
  }

  &.bg-light-warning {
    background-color: rgba($color: #ff9f43, $alpha: 0.12) !important;
  }

  &.bg-light-info {
    background-color: rgba($color: #00cfe8, $alpha: 0.12) !important;
  }
}

// Improved input field styling
.form-control, .vs__dropdown-toggle {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #B50100;
    box-shadow: none;
  }
}

// Enhance the filter section
.card {
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #5e5873;
  }
}

// Custom styles for v-select component
.select-search-option {
  .vs__dropdown-toggle {
    padding: 8px 0;
    border-color: #e0e0e0;
    background-color: #fff;
    border-radius: 4px;
  }

  .vs__selected {
    padding: 0 6px;
  }

  .vs__search {
    padding: 0 8px;
  }

  .vs__dropdown-menu {
    border-radius: 4px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .vs--open .vs__dropdown-toggle {
    border-color: #B50100;
  }
}

// Improved button styling
.btn-outline-primary {
  color: #B50100;
  border-color: #B50100;

  &:hover {
    background-color: #B50100;
    color: #fff;
  }
}

// Badge styling to match the image
.badge-round {
  padding: 0.3rem 0.65rem;
  font-weight: 500;
}

// Add styling for the badge in the title
.badge-round {
  font-size: 0.9rem;
  font-weight: 500;
  background-color: #B50100 !important;
}

// Match button styling from image
.btn-primary {
  background-color: #B50100 !important;
  border-color: #B50100 !important;
}

// Visually hidden class for accessibility
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>
